import {
  AREA_PRESETS,
  BUILDING_DEFAULTS,
  LOCATION_TYPE_LABELS,
  RACK_KIND_LABELS,
  RACK_PRESETS,
  blockProblems,
  buildingDoor,
  buildingFootprint,
  footprintsOverlap,
  lowerLabel,
  moveRacks,
  overlappingRacks,
  planBlock,
  rackFootprint,
  resizeRow,
  rowGap,
  rowLetters,
  turnRacks,
  type AisleUse,
  type AreaPreset,
  type BlockInput,
  type Coordinates3D,
  type FloorBuilding,
  type MapBin,
  type RackGeometry,
  type WarehouseMap,
} from '@wms/domain';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, errorMessage } from '../../../api';
import { fmt } from '../../../format';
import type { WarehouseView } from '../../../types';
import { ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../../ui';
import { Icon } from '../../../ui/icons';
import { buildWorld, rackKeyOf, type Cell } from '../../../ui/map/geometry';
import { MapView, type CellPaint, type MapDrag, type MapHit } from '../../../ui/map/MapView';
import {
  AreaFormView,
  BuildingFormView,
  EditorHelp,
  MetreField,
  RowFormView,
  SizeFormView,
  areaBlock,
  areaFormFor,
  buildingFormOf,
  buildingOf,
  letterChoices,
  metreText,
  metres,
  rowBlock,
  rowFormFor,
  sizeFormOf,
  skippedNote,
  type AreaForm,
  type BuildingForm,
  type RowForm,
  type SizeForm,
} from './EditorForms';

type Mode = 'idle' | 'rows' | 'areas' | 'edit' | 'building' | 'buildingEdit';

/** What the last change did, shown under the map until the next one. */
interface Change {
  readonly title: string;
  readonly codes: readonly string[];
  readonly points: readonly Coordinates3D[];
}

/** A row with a new letter or an area with a new code or name. */
interface RenameResult {
  readonly name: string;
  readonly codes: readonly string[];
  readonly onHand: number;
  readonly station: string | null;
}

interface BlockPreview {
  readonly count: number;
  readonly existing: number;
  readonly existingCodes: readonly string[];
}

/** What a drag started from: the block's corner, the selected racks, or the route start. */
type DragBase =
  | { readonly kind: 'origin'; readonly x: number; readonly y: number }
  | { readonly kind: 'racks'; readonly racks: readonly RackGeometry[] }
  | { readonly kind: 'depot'; readonly at: Coordinates3D }
  | { readonly kind: 'building'; readonly x: number; readonly y: number };

/** The building being added, until it is saved with an id of its own. */
const NEW_BUILDING = 'new';

const snap = (v: number) => Math.round(v * 10) / 10;
const pad2 = (n: number) => String(n).padStart(2, '0');
const keyOf = (r: { readonly aisle: string; readonly rack: number }) => rackKeyOf(r.aisle, r.rack);
const nameOf = (r: { readonly aisle: string; readonly rack: number }) => `${r.aisle}-${pad2(r.rack)}`;
const cornersOf = (racks: readonly RackGeometry[]): Coordinates3D[] =>
  racks.flatMap((r) => {
    const f = rackFootprint(r);
    return [
      { x: f.x0, y: f.y0, z: 0 },
      { x: f.x1, y: f.y1, z: Math.max(0.1, r.levelHeight) },
    ];
  });

/**
 * The floor plan to build the warehouse on: rows of racks and floor areas added at their real
 * size and placed by dragging, existing ones moved, turned, resized or removed; the route start.
 */
export function WarehouseEditor({ warehouse, onChanged, onPrint }: { warehouse: WarehouseView; onChanged: () => void; onPrint: (codes: readonly string[]) => void }) {
  const toast = useToast();
  const axis = warehouse.layout.aisleAxis ?? 'y';
  const map = useLoad(() => api<WarehouseMap>('GET', `/api/wms/map?warehouseId=${warehouse.id}`), [warehouse.id]);
  const [mode, setMode] = useState<Mode>('idle');
  const [rowForm, setRowForm] = useState<RowForm>(() => rowFormFor('shelf'));
  const [areaForm, setAreaForm] = useState<AreaForm>(() => areaFormFor('packing'));
  const [onExisting, setOnExisting] = useState<'skip' | 'update'>('skip');
  const [preview, setPreview] = useState<BlockPreview | null>(null);
  const [selection, setSelection] = useState<readonly string[]>([]);
  const [edits, setEdits] = useState<ReadonlyMap<string, RackGeometry>>(new Map());
  const [sizeForm, setSizeForm] = useState<SizeForm | null>(null);
  const [depot, setDepot] = useState<Coordinates3D | null>(warehouse.layout.depot ?? null);
  const [recent, setRecent] = useState<ReadonlySet<string>>(new Set());
  const [change, setChange] = useState<Change | null>(null);
  const [view, setView] = useState<{ key: string; points: readonly Coordinates3D[] }>({ key: 'all', points: [] });
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [buildingForm, setBuildingForm] = useState<BuildingForm>(() => buildingFormOf({ ...BUILDING_DEFAULTS, x: 0, y: 0 }, true));
  // the building added (NEW_BUILDING) or edited, in modes 'building' and 'buildingEdit'
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [removingBuilding, setRemovingBuilding] = useState(false);
  const dragBase = useRef<DragBase | null>(null);

  const data = map.data;
  const bins = useMemo(() => data?.bins ?? [], [data]);
  const existingCodes = useMemo(() => new Set(bins.map((b) => b.code)), [bins]);
  const baseRacks = useMemo(() => new Map((data?.racks ?? []).map((r) => [keyOf(r), r])), [data]);
  const racksNow = useMemo(() => {
    const all = new Map(baseRacks);
    for (const [k, r] of edits) all.set(k, r);
    return all;
  }, [baseRacks, edits]);
  // letters in use: all the server counts (rows switched off too) and what the map shows since
  const uses = useMemo(() => {
    const all = new Map(warehouse.aisles.map((u) => [u.aisle, u]));
    for (const r of baseRacks.values()) {
      if (!all.has(r.aisle)) all.set(r.aisle, { aisle: r.aisle, kind: r.kind, label: RACK_KIND_LABELS[r.kind], bins: 0, inactive: 0, lastRack: r.rack });
    }
    return [...all.values()];
  }, [warehouse.aisles, baseRacks]);

  // a new block: the first free row letters side by side; area numbers after the last area
  const lettering = useMemo(() => rowLetters(uses, rowForm.rows, rowForm.start), [uses, rowForm.rows, rowForm.start]);
  const letters = lettering.letters;
  const letterOptions = useMemo(() => letterChoices(uses, rowForm.rows), [uses, rowForm.rows]);
  const letterNote = useMemo(() => (rowForm.start ? null : skippedNote(uses, letters, rowForm.rows)), [uses, letters, rowForm.start, rowForm.rows]);
  const areaCode = AREA_PRESETS[areaForm.type].code;
  const areaFirst = useMemo(
    () => Math.max(0, ...uses.filter((u) => u.aisle === areaCode).map((u) => u.lastRack), ...bins.filter((b) => b.aisle === areaCode).map((b) => b.rack)) + 1,
    [uses, bins, areaCode],
  );
  const block: BlockInput | null = mode === 'rows' ? rowBlock(rowForm, letters, axis) : mode === 'areas' ? areaBlock(areaForm, areaFirst, axis) : null;
  const problems = useMemo(() => {
    if (!block) return [];
    if (block.kind !== 'area') {
      const blocked = uses.find((u) => u.aisle === lettering.blockedBy);
      if (blocked) return [`${blocked.aisle} harfi ${lowerLabel(blocked.label)} için kullanılıyor; başka bir ilk harf seçin ya da sıra sayısını azaltın`];
      if (letters.length < rowForm.rows) return ['Bu kadar boş sıra harfi kalmadı'];
    }
    return blockProblems(block);
  }, [block, uses, lettering.blockedBy, letters.length, rowForm.rows]);
  const plan = useMemo(() => (block && problems.length === 0 ? planBlock(block) : null), [block, problems]);
  const blockJson = block && problems.length === 0 ? JSON.stringify(block) : null;

  // which of the planned bins exist already: asked once the form settles
  useEffect(() => {
    setPreview(null);
    if (!blockJson) return;
    const timer = setTimeout(() => {
      api<BlockPreview>('POST', `/api/admin/warehouses/${warehouse.id}/blocks/preview`, JSON.parse(blockJson) as BlockInput)
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 450);
    return () => clearTimeout(timer);
  }, [blockJson, warehouse.id]);

  const plannedKeys = useMemo(() => new Set((plan?.racks ?? []).map(keyOf)), [plan]);
  const plannedNew = useMemo(() => (plan?.racks ?? []).filter((r) => !baseRacks.has(keyOf(r)) || onExisting === 'update'), [plan, baseRacks, onExisting]);
  const overlaps = useMemo(() => {
    const others = [...racksNow.values()].filter((r) => !plannedKeys.has(keyOf(r)));
    const moved = [...edits.values()];
    const otherThanMoved = [...racksNow.values()].filter((r) => !edits.has(keyOf(r)));
    return new Set([...overlappingRacks(plannedNew, others), ...overlappingRacks(moved, otherThanMoved)].map(keyOf));
  }, [plannedNew, racksNow, plannedKeys, edits]);
  const selected = useMemo(() => new Set(selection), [selection]);

  // buildings (the office): the saved ones, the one being added or edited as its form says
  const savedBuildings = useMemo(() => data?.layout.buildings ?? [], [data]);
  const formBuilding = useMemo(
    () => (buildingId && (mode === 'building' || mode === 'buildingEdit') ? buildingOf(buildingId, buildingForm) : null),
    [buildingId, buildingForm, mode],
  );
  const buildingsNow = useMemo(() => {
    const others = savedBuildings.filter((b) => b.id !== formBuilding?.id);
    return formBuilding ? [...others, formBuilding] : others;
  }, [savedBuildings, formBuilding]);
  const buildingOverlap = useMemo(() => {
    if (!formBuilding) return false;
    const f = buildingFootprint(formBuilding);
    return [...racksNow.values()].some((r) => footprintsOverlap(f, rackFootprint(r))) || savedBuildings.some((b) => b.id !== formBuilding.id && footprintsOverlap(f, buildingFootprint(b)));
  }, [formBuilding, racksNow, savedBuildings]);
  // the middle of the racks: a building's door is the side that looks toward it
  const racksCenter = useMemo(() => {
    const f = [...racksNow.values()].filter((r) => r.kind !== 'area').map(rackFootprint);
    if (f.length === 0) return null;
    return { x: (Math.min(...f.map((q) => q.x0)) + Math.max(...f.map((q) => q.x1))) / 2, y: (Math.min(...f.map((q) => q.y0)) + Math.max(...f.map((q) => q.y1))) / 2 };
  }, [racksNow]);

  const world = useMemo(() => {
    if (!data) return null;
    const racks = new Map(racksNow);
    const drawnBins: MapBin[] = [...data.bins];
    for (const r of plannedNew) racks.set(keyOf(r), r);
    for (const b of plan?.bins ?? []) {
      if (!existingCodes.has(b.code)) drawnBins.push({ code: b.code, aisle: b.aisle, rack: b.rack, level: b.level, bin: b.bin, type: b.type, at: { x: b.x, y: b.y, z: b.z }, isBlocked: false, maxVolumeCm3: b.maxVolumeCm3 });
    }
    return buildWorld({ ...data, layout: { ...data.layout, buildings: buildingsNow }, racks: [...racks.values()], bins: drawnBins });
  }, [data, racksNow, plannedNew, plan, existingCodes, buildingsNow]);

  const paint = useCallback(
    (cell: Cell): CellPaint | undefined => {
      if (cell.building) {
        if (cell.building !== formBuilding?.id) return undefined;
        const adding = cell.building === NEW_BUILDING;
        return {
          fill: buildingOverlap ? 'var(--bad)' : adding ? 'var(--brand)' : undefined,
          className: `selected movable${adding ? ' planned' : ''}`,
          title: `${cell.label}${buildingOverlap ? ' · rafların üstüne geliyor' : adding ? ' · eklenecek' : ''}`,
        };
      }
      const key = cell.column;
      const classes: string[] = [];
      let fill: string | undefined;
      let note: string | null = null;
      if (plannedKeys.has(key)) {
        const fresh = cell.codes.some((c) => !existingCodes.has(c));
        classes.push('planned', 'movable');
        fill = overlaps.has(key) ? 'var(--bad)' : fresh ? 'var(--brand)' : 'var(--info)';
        note = overlaps.has(key) ? 'başka bir rafın üstüne geliyor' : fresh ? 'eklenecek' : onExisting === 'skip' ? 'zaten var, dokunulmayacak' : 'zaten var, güncellenecek';
      }
      if (selected.has(key)) {
        classes.push('selected', 'movable');
        if (edits.has(key)) {
          fill = overlaps.has(key) ? 'var(--bad)' : 'var(--info)';
          note = overlaps.has(key) ? 'başka bir rafın üstüne geliyor' : 'değişti, kaydedilmedi';
        }
      } else if (edits.has(key)) {
        fill = 'var(--info)';
        note = 'değişti, kaydedilmedi';
      }
      if (cell.codes.some((c) => recent.has(c))) {
        classes.push('recent');
        fill ??= 'var(--map-done)';
        note ??= 'yeni eklendi';
      }
      if (classes.length === 0 && !fill) return undefined;
      return { fill, className: classes.join(' '), title: `${cell.label}${note ? ` · ${note}` : ''}` };
    },
    [plannedKeys, existingCodes, overlaps, onExisting, selected, edits, recent, formBuilding, buildingOverlap],
  );

  // the block being added is framed once when it shows up; dragging it must not move the camera
  const planPoints = useMemo(() => cornersOf(plan?.racks ?? []), [plan]);
  const framedPlan = useRef(false);
  useEffect(() => {
    if (mode !== 'rows' && mode !== 'areas') framedPlan.current = false;
    else if (plan && !framedPlan.current) {
      framedPlan.current = true;
      setView({ key: `plan:${Date.now()}`, points: [...planPoints, ...cornersOf([...racksNow.values()])] });
    }
  }, [mode, plan, planPoints, racksNow]);

  const rowKeys = useCallback((aisle: string) => [...racksNow.values()].filter((r) => r.aisle === aisle).map(keyOf), [racksNow]);
  const selectedRacks = useMemo(() => selection.map((k) => racksNow.get(k)).filter((r): r is RackGeometry => r !== undefined), [selection, racksNow]);

  const select = (keys: readonly string[]) => {
    setBuildingId(null);
    setRemovingBuilding(false);
    setSelection(keys);
    setMode(keys.length > 0 ? 'edit' : 'idle');
    const first = keys[0] ? racksNow.get(keys[0]) : undefined;
    const row = keys.map((k) => racksNow.get(k)).filter((r): r is RackGeometry => r !== undefined);
    setSizeForm(first ? sizeFormOf(first, rowGap(row)) : null);
  };

  const onTap = (hit: MapHit | null) => {
    if (mode === 'rows' || mode === 'areas' || mode === 'building') return;
    if (hit?.kind === 'building') {
      const b = savedBuildings.find((x) => x.id === hit.id);
      if (b) editBuilding(b);
      return;
    }
    if (hit?.kind !== 'cell') {
      if (edits.size === 0) select([]);
      return;
    }
    const g = racksNow.get(hit.cell.column);
    if (!g) return;
    // the first tap takes the whole row, a second one on it narrows to that position
    if (g.kind === 'area') select([keyOf(g)]);
    else if (selection.length > 1 && selected.has(keyOf(g))) select([keyOf(g)]);
    else select(rowKeys(g.aisle));
  };

  const drag: MapDrag = {
    can: (hit) => {
      if (hit?.kind === 'pin') return hit.id === 'depot' && (mode === 'idle' || mode === 'edit');
      if (hit?.kind === 'building') return (mode === 'building' || mode === 'buildingEdit') && hit.id === buildingId;
      if (hit?.kind !== 'cell') return false;
      if (mode === 'rows' || mode === 'areas') return plannedKeys.has(hit.cell.column);
      return mode === 'edit' && selected.has(hit.cell.column);
    },
    move: (hit, offset, done) => {
      if (!dragBase.current) {
        if (hit.kind === 'pin') dragBase.current = { kind: 'depot', at: depot ?? { x: 0, y: 0, z: 0 } };
        else if (hit.kind === 'building') dragBase.current = { kind: 'building', x: metres(buildingForm.x), y: metres(buildingForm.y) };
        else if (mode === 'rows') dragBase.current = { kind: 'origin', x: metres(rowForm.x), y: metres(rowForm.y) };
        else if (mode === 'areas') dragBase.current = { kind: 'origin', x: metres(areaForm.x), y: metres(areaForm.y) };
        else dragBase.current = { kind: 'racks', racks: selectedRacks };
      }
      const base = dragBase.current;
      const dx = snap(offset.x);
      const dy = snap(offset.y);
      if (base.kind === 'origin') {
        const x = metreText(snap(base.x + dx));
        const y = metreText(snap(base.y + dy));
        if (mode === 'rows') setRowForm((f) => ({ ...f, x, y }));
        else setAreaForm((f) => ({ ...f, x, y }));
      } else if (base.kind === 'building') {
        setBuildingForm((f) => ({ ...f, x: metreText(snap(base.x + dx)), y: metreText(snap(base.y + dy)) }));
      } else if (base.kind === 'racks') {
        const moved = moveRacks(base.racks, dx, dy);
        setEdits((prev) => {
          const next = new Map(prev);
          for (const r of moved) next.set(keyOf(r), r);
          return next;
        });
      } else {
        setDepot({ x: snap(base.at.x + dx), y: snap(base.at.y + dy), z: 0 });
      }
      if (done) dragBase.current = null;
    },
  };

  const showChange = (title: string, codes: readonly string[], racks: readonly RackGeometry[]) => {
    const points = cornersOf(racks);
    setChange({ title, codes, points });
    setRecent(new Set(codes));
    setView({ key: `change:${Date.now()}`, points });
  };
  useEffect(() => {
    if (recent.size === 0) return;
    const timer = setTimeout(() => setRecent(new Set()), 6_000);
    return () => clearTimeout(timer);
  }, [recent]);

  const editBuilding = (b: FloorBuilding) => {
    setSelection([]);
    setBuildingId(b.id);
    setBuildingForm(buildingFormOf(b));
    setRemovingBuilding(false);
    setMode('buildingEdit');
  };
  const closeBuilding = () => {
    setBuildingId(null);
    setRemovingBuilding(false);
    setMode('idle');
  };
  const doorOf = (b: FloorBuilding) => buildingDoor(b, racksCenter ?? { x: b.x + 1, y: b.y });
  /** Stores the buildings and, when given, puts the route start (P) there too. */
  const saveBuildings = async (list: readonly FloorBuilding[], message: string, start?: Coordinates3D) => {
    setBusy(true);
    try {
      await api('PUT', `/api/admin/warehouses/${warehouse.id}/buildings`, { buildings: list });
      if (start) {
        await api('PUT', `/api/admin/warehouses/${warehouse.id}/layout`, { aisleAxis: axis, crossAisles: warehouse.layout.crossAisles ?? [], depot: start });
        setDepot(start);
      }
      toast(message, 'ok');
      closeBuilding();
      await map.reload();
      onChanged();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const addBuilding = () => {
    if (!formBuilding) return;
    const b: FloorBuilding = { ...formBuilding, id: `b-${Date.now().toString(36)}` };
    const start = buildingForm.startHere ? doorOf(b) : undefined;
    void saveBuildings([...savedBuildings, b], `${b.name} eklendi${start ? '; toplama rotaları artık kapısından başlıyor' : ''}`, start);
  };
  const saveBuilding = (start: boolean) => {
    if (!formBuilding) return;
    const list = savedBuildings.map((b) => (b.id === formBuilding.id ? formBuilding : b));
    void saveBuildings(list, start ? `Toplama rotaları artık ${formBuilding.name} kapısından başlıyor` : `${formBuilding.name} kaydedildi`, start ? doorOf(formBuilding) : undefined);
  };
  const removeBuilding = () => {
    const b = savedBuildings.find((x) => x.id === buildingId);
    if (b) void saveBuildings(savedBuildings.filter((x) => x.id !== b.id), `${b.name} silindi`);
  };

  const add = async () => {
    if (!block || !plan) return;
    setBusy(true);
    try {
      const r = await api<{ created: number; updated: number; skipped: number; createdCodes: string[]; stations: string[] }>('POST', `/api/admin/warehouses/${warehouse.id}/blocks`, { block, onExisting });
      const what =
        block.kind === 'area'
          ? `${AREA_PRESETS[block.type].label}: ${plan.racks.map(nameOf).join(', ')}`
          : `${block.aisles.join(', ')} ${block.aisles.length > 1 ? 'sıraları' : 'sırası'} (${lowerLabel(RACK_PRESETS[block.kind].label)})`;
      const counts = [`${r.created} göz eklendi`, r.updated ? `${r.updated} göz güncellendi` : '', r.skipped ? `${r.skipped} göz zaten vardı` : ''].filter(Boolean).join(', ');
      toast(`${what}: ${counts}${r.stations.length ? ` · paketleme istasyonu açıldı: ${r.stations.join(', ')}` : ''}`, 'ok');
      showChange(`${what} · ${counts}`, r.createdCodes, plan.racks);
      setMode('idle');
      // the chosen first letter is taken now: the next block goes back to the automatic one
      setRowForm((f) => ({ ...f, start: '' }));
      await map.reload();
      onChanged();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const racks = [...edits.values()];
    if (racks.length === 0) return;
    setBusy(true);
    try {
      const r = await api<{ racks: number; bins: number }>('PUT', `/api/admin/warehouses/${warehouse.id}/racks`, { racks });
      const keys = new Set(racks.map(keyOf));
      const codes = bins.filter((b) => keys.has(keyOf(b))).map((b) => b.code);
      toast(`${r.racks} raf kaydedildi; ${r.bins} gözün yeri güncellendi, toplama rotaları yeni yerlere göre hesaplanır`, 'ok');
      showChange(`${describe(racks)} kaydedildi · ${r.bins} gözün yeri güncellendi`, codes, racks);
      setEdits(new Map());
      await map.reload();
      onChanged();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  // a row's letter and an area's code and name; their bins get new codes, so the labels too
  const afterRename = async (title: string, codes: readonly string[], racks: readonly RackGeometry[]) => {
    showChange(title, codes, racks);
    select([]);
    await map.reload();
    onChanged();
  };
  const renameRow = async (from: string, to: string) => {
    const racks = [...racksNow.values()].filter((r) => r.aisle === from);
    setBusy(true);
    try {
      const r = await api<RenameResult>('POST', `/api/admin/warehouses/${warehouse.id}/rows/rename`, { from, to });
      toast(`${from} sırası artık ${to}: ${r.codes.length} gözün kodu değişti, etiketlerini yeniden basın`, 'ok');
      await afterRename(`${from} sırası → ${to} sırası · ${r.codes.length} göz yeni kod aldı${r.onHand ? `, içlerinde ${fmt.n(r.onHand)} adet ürün var` : ''}`, r.codes, racks);
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const editArea = async (area: RackGeometry, change: { number?: number; name?: string | null }) => {
    setBusy(true);
    try {
      const r = await api<RenameResult>('PATCH', `/api/admin/warehouses/${warehouse.id}/areas`, { aisle: area.aisle, rack: area.rack, ...change });
      const was = nameOf(area);
      toast(`${was === r.name ? r.name : `${was} → ${r.name}`} kaydedildi${r.station ? ` · paketleme istasyonu ${r.station}` : ''}`, 'ok');
      await afterRename(was === r.name ? `${r.name} kaydedildi` : `${was} artık ${r.name}; etiketini yeniden basın`, r.codes, [area]);
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  // areas built on the old screen with row letters: each moves to its own code, freeing the letter
  const oldAreas = [...racksNow.values()].flatMap((r) => {
    if (r.kind !== 'area') return [];
    const type = bins.find((b) => b.aisle === r.aisle && b.rack === r.rack)?.type;
    const preset = type ? (AREA_PRESETS as Partial<Record<string, AreaPreset>>)[type] : undefined;
    return preset && preset.code !== r.aisle ? [{ area: r, preset }] : [];
  });
  const fixOldAreas = async () => {
    setBusy(true);
    const done: string[] = [];
    try {
      for (const { area } of oldAreas) {
        const r = await api<RenameResult>('PATCH', `/api/admin/warehouses/${warehouse.id}/areas`, { aisle: area.aisle, rack: area.rack });
        done.push(`${nameOf(area)} → ${r.name}`);
      }
      toast(`Alanlar kendi kodlarını aldı: ${done.join(', ')}. Etiketlerini yeniden basın.`, 'ok');
    } catch (e) {
      toast(`${done.length ? `${done.join(', ')} yapıldı; ` : ''}${errorMessage(e)}`, 'bad');
    } finally {
      setBusy(false);
      setChange(null);
      await map.reload();
      onChanged();
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await api<{ removed: number; refused: { code: string; reason: string }[] }>('POST', `/api/admin/warehouses/${warehouse.id}/racks/remove`, {
        racks: selectedRacks.map((g) => ({ aisle: g.aisle, rack: g.rack })),
      });
      toast(
        `${r.removed} göz silindi${r.refused.length ? `; silinemeyen: ${r.refused.map((x) => x.code).join(', ')} (${r.refused[0]!.reason})` : ''}`,
        r.refused.length ? 'info' : 'ok',
      );
      setRemoving(false);
      setEdits((prev) => {
        const next = new Map(prev);
        for (const k of selection) next.delete(k);
        return next;
      });
      select([]);
      setChange(null);
      await map.reload();
      onChanged();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  if (map.error) return <ErrorBanner message={map.error} onRetry={() => void map.reload()} />;
  if (!data || !world) return <Spinner />;

  const storageRows = new Set([...racksNow.values()].filter((r) => r.kind !== 'area').map((r) => r.aisle));
  const areaCount = [...racksNow.values()].filter((r) => r.kind === 'area').length;
  const startRows = () => {
    // next to what is there already, at the front of it
    const f = [...racksNow.values()].map(rackFootprint);
    const x = f.length ? Math.ceil(Math.max(...f.map((p) => (axis === 'y' ? p.x1 : p.y1))) + 1) : 1;
    const y = f.length ? Math.floor(Math.min(...f.map((p) => (axis === 'y' ? p.y0 : p.x0)))) : 1;
    setRowForm((form) => ({ ...form, x: metreText(axis === 'y' ? x : y), y: metreText(axis === 'y' ? y : x) }));
    setSelection([]);
    setMode('rows');
  };
  const startAreas = () => {
    const f = [...racksNow.values()].map(rackFootprint);
    const x = f.length ? Math.floor(Math.min(...f.map((p) => p.x0))) : 0;
    const y = f.length ? Math.floor(Math.min(...f.map((p) => p.y0))) - 4 : 0;
    setAreaForm((form) => ({ ...form, x: metreText(x), y: metreText(y) }));
    setSelection([]);
    setMode('areas');
  };

  const startBuilding = () => {
    // to the left of what is there, its bottom edge level with the racks'
    const taken = [...[...racksNow.values()].map(rackFootprint), ...savedBuildings.map(buildingFootprint)];
    const { width, depth } = BUILDING_DEFAULTS;
    const x0 = taken.length ? Math.floor(Math.min(...taken.map((f) => f.x0))) - width - 2 : 0;
    const y0 = taken.length ? Math.floor(Math.min(...taken.map((f) => f.y0))) : 0;
    const name = savedBuildings.length === 0 ? BUILDING_DEFAULTS.name : 'Yeni bina';
    setBuildingForm(buildingFormOf({ ...BUILDING_DEFAULTS, name, x: x0 + width / 2, y: y0 + depth / 2 }, savedBuildings.length === 0));
    setSelection([]);
    setRemovingBuilding(false);
    setBuildingId(NEW_BUILDING);
    setMode('building');
    const corners = [...taken.map((f) => ({ x: f.x0, y: f.y0, z: 0 })), ...taken.map((f) => ({ x: f.x1, y: f.y1, z: 0 }))];
    setView({ key: `building:${Date.now()}`, points: [...corners, { x: x0, y: y0, z: 0 }, { x: x0 + width, y: y0 + depth, z: BUILDING_DEFAULTS.height }] });
  };
  const savedBuilding = savedBuildings.find((b) => b.id === buildingId);
  // field by field: the stored layout (jsonb) comes back with its keys in another order
  const buildingChanged =
    mode === 'buildingEdit' &&
    formBuilding !== null &&
    (!savedBuilding || (['name', 'x', 'y', 'width', 'depth', 'height'] as const).some((k) => formBuilding[k] !== savedBuilding[k]));

  const panel =
    mode === 'building' || mode === 'buildingEdit' ? (
      <div className="stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3>{mode === 'building' ? 'Bina ekle' : `${savedBuilding?.name ?? 'Bina'} · bina`}</h3>
          <button className="btn sm ghost" onClick={closeBuilding} aria-label="Kapat">
            {mode === 'building' ? 'Vazgeç' : <Icon name="x" size={16} />}
          </button>
        </div>
        <BuildingFormView form={buildingForm} onChange={setBuildingForm} adding={mode === 'building'} />
        {!formBuilding && <div className="banner warn">Adını ve ölçülerini girin.</div>}
        {buildingOverlap && <div className="banner bad">Bina rafların ya da başka bir binanın üstüne geliyor; sürükleyerek boş bir yere taşıyın.</div>}
        {mode === 'building' ? (
          <div className="row">
            <button className="btn primary" disabled={busy || !formBuilding} onClick={addBuilding}>
              <Icon name="check" /> Ekle
            </button>
          </div>
        ) : (
          <>
            <div className="row">
              <button className="btn primary" disabled={busy || !buildingChanged} onClick={() => saveBuilding(false)}>
                <Icon name="check" /> Kaydet
              </button>
              <button
                className="btn"
                disabled={!formBuilding}
                onClick={() => formBuilding && setBuildingForm((f) => buildingFormOf({ ...formBuilding, width: formBuilding.depth, depth: formBuilding.width }, f.startHere))}
              >
                <Icon name="flip" size={16} /> 90° çevir
              </button>
            </div>
            <div className="row">
              <button className="btn" disabled={busy || !formBuilding} onClick={() => saveBuilding(true)}>
                <Icon name="route" size={16} /> Rota başlangıcı burası
              </button>
              <button className="btn ghost danger-text" onClick={() => setRemovingBuilding(true)}>
                <Icon name="trash" size={16} /> Sil
              </button>
            </div>
            <span className="hint">“Rota başlangıcı burası”, P’yi binanın raflara bakan kapısına koyar; toplama rotaları oradan başlar ve oraya döner.</span>
            {removingBuilding && (
              <div className="banner warn">
                <div>
                  {savedBuilding?.name ?? 'Bina'} haritadan silinsin mi?
                  <div className="row" style={{ marginTop: 6 }}>
                    <button className="btn sm danger" disabled={busy} onClick={removeBuilding}>
                      Sil
                    </button>
                    <button className="btn sm" onClick={() => setRemovingBuilding(false)}>
                      Vazgeç
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    ) : mode === 'rows' || mode === 'areas' ? (
      <div className="stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3>{mode === 'rows' ? 'Raf sırası ekle' : 'Alan ekle'}</h3>
          <button className="btn sm ghost" onClick={() => setMode('idle')}>
            Vazgeç
          </button>
        </div>
        {mode === 'rows' ? (
          <RowFormView form={rowForm} onChange={setRowForm} letters={letters} options={letterOptions} note={letterNote} />
        ) : (
          <AreaFormView form={areaForm} onChange={setAreaForm} codes={(plan?.racks ?? []).map(nameOf)} />
        )}
        {problems.length > 0 ? (
          <div className="banner warn">{problems.join(' · ')}</div>
        ) : plan ? (
          <div className="banner info-soft">
            <Icon name="grid" />
            <div>
              <b>{fmt.n(plan.bins.length)} göz</b>: {plan.bins[0]?.code} … {plan.bins.at(-1)?.code}
              <div style={{ fontWeight: 500 }}>
                Kapladığı yer {metreText(plan.extent.x1 - plan.extent.x0)} × {metreText(plan.extent.y1 - plan.extent.y0)} m (koridorlar dahil)
              </div>
              {preview && preview.existing > 0 && (
                <div style={{ fontWeight: 500 }}>
                  {preview.existing} göz zaten var ({preview.existingCodes.slice(0, 4).join(', ')}
                  {preview.existing > 4 ? '…' : ''}).
                </div>
              )}
            </div>
          </div>
        ) : null}
        {plannedNew.some((r) => overlaps.has(keyOf(r))) && (
          <div className="banner bad">Kırmızı raflar başka rafların veya alanların üstüne geliyor; bloğu sürükleyerek boş bir yere taşıyın.</div>
        )}
        {preview && preview.existing > 0 && (
          <div className="choice-list">
            <label className={`choice${onExisting === 'skip' ? ' on' : ''}`}>
              <input type="radio" name="existing" checked={onExisting === 'skip'} onChange={() => setOnExisting('skip')} />
              <span>
                <b>Var olanlara dokunma</b>
                <small>Yalnız eksik gözler eklenir</small>
              </span>
            </label>
            <label className={`choice${onExisting === 'update' ? ' on' : ''}`}>
              <input type="radio" name="existing" checked={onExisting === 'update'} onChange={() => setOnExisting('update')} />
              <span>
                <b>Var olanları da bu ölçülere ve yere getir</b>
                <small>İçinde stok olan gözlerin türü değişmez</small>
              </span>
            </label>
          </div>
        )}
        <div className="row">
          <button className="btn primary" disabled={busy || !plan || (preview !== null && preview.existing === preview.count && onExisting === 'skip')} onClick={() => void add()}>
            <Icon name="check" /> Ekle
          </button>
        </div>
      </div>
    ) : mode === 'edit' && selectedRacks.length > 0 ? (
      <SelectionPanel
        racks={selectedRacks}
        allBins={bins}
        sizeForm={sizeForm}
        onSizeForm={setSizeForm}
        edited={edits.size}
        busy={busy}
        onClose={() => select([])}
        onRow={() => select(rowKeys(selectedRacks[0]!.aisle))}
        onMoveTo={(x, y) => {
          const f = selectedRacks.map(rackFootprint);
          const dx = x - Math.min(...f.map((p) => p.x0));
          const dy = y - Math.min(...f.map((p) => p.y0));
          setEdits((prev) => new Map([...prev, ...moveRacks(selectedRacks, dx, dy).map((r) => [keyOf(r), r] as const)]));
        }}
        onTurn={() => setEdits((prev) => new Map([...prev, ...turnRacks(selectedRacks).map((r) => [keyOf(r), r] as const)]))}
        onResize={(size) => setEdits((prev) => new Map([...prev, ...resizeRow(selectedRacks, size).map((r) => [keyOf(r), r] as const)]))}
        onSave={() => void save()}
        onDiscard={() => {
          setEdits(new Map());
          select(selection);
        }}
        onPrint={() => onPrint(bins.filter((b) => selected.has(keyOf(b))).map((b) => b.code))}
        onRemove={() => setRemoving(true)}
        uses={uses}
        onRenameRow={(to) => void renameRow(selectedRacks[0]!.aisle, to)}
        onEditArea={(change) => void editArea(selectedRacks[0]!, change)}
      />
    ) : (
      <div className="stack">
        <EditorHelp open={bins.length === 0} />
        {oldAreas.length > 0 && (
          <div className="banner warn">
            <div>
              <b>{oldAreas.map(({ area, preset }) => `${area.aisle} (${lowerLabel(preset.label)})`).join(', ')}</b>{' '}
              {oldAreas.length > 1 ? 'alanları' : 'alanı'} eski ekranda sıra harfiyle açılmış; bu harfler raf sıralarına verilemiyor. Alanlar kendi kodlarını alınca (
              {[...new Set(oldAreas.map(({ preset }) => `${preset.code}-01`))].join(', ')} gibi) harfler sıralara kalır; alanların etiketlerini yeniden basmanız gerekir.
              <div className="row" style={{ marginTop: 6 }}>
                <button className="btn sm primary" disabled={busy} onClick={() => void fixOldAreas()}>
                  Alanları kendi koduna çevir
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="kpi-grid">
          <div>
            <b>{fmt.n(storageRows.size)}</b>
            <span>raf sırası</span>
          </div>
          <div>
            <b>{fmt.n([...racksNow.values()].filter((r) => r.kind !== 'area').length)}</b>
            <span>raf / palet yeri</span>
          </div>
          <div>
            <b>{fmt.n(areaCount)}</b>
            <span>alan</span>
          </div>
          <div>
            <b>{fmt.n(bins.length)}</b>
            <span>aktif göz</span>
          </div>
        </div>
        <div className="row">
          <button className="btn primary" onClick={startRows}>
            <Icon name="plus" /> Raf sırası ekle
          </button>
          <button className="btn" onClick={startAreas}>
            <Icon name="plus" /> Alan ekle
          </button>
          <button className="btn" onClick={startBuilding}>
            <Icon name="building" /> Bina ekle
          </button>
        </div>
        <span className="hint">Bir rafa, alana veya binaya dokunun: taşıyın, yönünü çevirin, ölçülerini değiştirin.</span>
        {edits.size > 0 && (
          <div className="banner warn">
            <div>
              {describe([...edits.values()])} değişti, henüz kaydedilmedi.
              <div className="row" style={{ marginTop: 6 }}>
                <button className="btn sm primary" disabled={busy} onClick={() => void save()}>
                  Kaydet
                </button>
                <button className="btn sm" onClick={() => setEdits(new Map())}>
                  Geri al
                </button>
              </div>
            </div>
          </div>
        )}
        <RouteSettings warehouse={warehouse} racks={[...racksNow.values()]} depot={depot} onDepot={setDepot} onSaved={onChanged} />
      </div>
    );

  return (
    <div className="builder-layout">
      <section className="card">
        <div className="card-b">{panel}</div>
      </section>
      <section className="card map-card">
        <div className="card-h">
          <Icon name="map" />
          <h2>Kat planı</h2>
          <span className="sub">
            {mode === 'rows' || mode === 'areas'
              ? 'Yeni bloğu sürükleyerek yerleştirin'
              : mode === 'building'
                ? 'Binayı sürükleyerek yerleştirin'
                : mode === 'buildingEdit'
                  ? 'Binayı sürükleyerek taşıyın'
                  : mode === 'edit'
                    ? 'Seçili rafları sürükleyerek taşıyın'
                    : 'Bir rafa veya binaya dokunun · P’yi sürükleyin'}
          </span>
        </div>
        <MapView
          world={world}
          label="Depo kat planı"
          paint={paint}
          depot={depot}
          frame={view.points}
          frameKey={view.key}
          frames={plan ? [{ label: 'Yeni blok', title: 'Eklenecek blokları göster', points: planPoints }] : []}
          onTap={onTap}
          drag={drag}
          measureByDefault
          card={
            change && mode === 'idle' ? (
              <div className="map-pop" role="status">
                <div className="map-pop-h">
                  <Icon name="check" size={16} />
                  <b>Son değişiklik</b>
                  <button className="btn sm ghost" onClick={() => setChange(null)} aria-label="Kapat">
                    <Icon name="x" size={16} />
                  </button>
                </div>
                <p style={{ margin: '4px 0 6px' }}>{change.title}</p>
                {change.codes.length > 0 && (
                  <div className="row">
                    <span className="muted mono">
                      {change.codes[0]} … {change.codes.at(-1)}
                    </span>
                    <button className="btn sm" onClick={() => setView({ key: `change:${Date.now()}`, points: change.points })}>
                      Haritada göster
                    </button>
                    <button className="btn sm" onClick={() => onPrint(change.codes)}>
                      <Icon name="printer" size={16} /> Etiketlerini bas
                    </button>
                  </div>
                )}
              </div>
            ) : undefined
          }
          legend={
            <>
              <span>
                <i style={{ background: 'var(--brand)' }} /> Eklenecek
              </span>
              <span>
                <i style={{ background: 'var(--info)' }} /> Değişti / zaten var
              </span>
              <span>
                <i style={{ background: 'var(--bad)' }} /> Çakışıyor
              </span>
              <span>
                <i style={{ background: 'var(--map-done)' }} /> Yeni eklendi
              </span>
            </>
          }
        />
      </section>
      {removing && (
        <Modal
          title="Silinsin mi?"
          onClose={() => setRemoving(false)}
          footer={
            <>
              <button className="btn" onClick={() => setRemoving(false)}>
                Vazgeç
              </button>
              <button className="btn danger" disabled={busy} onClick={() => void remove()}>
                Sil
              </button>
            </>
          }
        >
          <p>
            {describe(selectedRacks)} ve içindeki {fmt.n(bins.filter((b) => selected.has(keyOf(b))).length)} göz silinecek. İçinde stok olmuş, sayılmış ya da
            paketleme yapılmış gözler silinmez; onları Gözler sekmesinden pasife alabilirsiniz.
          </p>
        </Modal>
      )}
    </div>
  );
}

/** "A sırası (8 raf)", "A-03", "PK-01 (Paketleme masası)". */
function describe(racks: readonly RackGeometry[]): string {
  if (racks.length === 0) return '';
  const first = racks[0]!;
  if (racks.length === 1) return first.kind === 'area' ? `${nameOf(first)}` : `${nameOf(first)} rafı`;
  const aisles = [...new Set(racks.map((r) => r.aisle))];
  return aisles.length === 1 ? `${aisles[0]} sırası (${racks.length} yer)` : `${aisles.join(', ')} sıraları (${racks.length} yer)`;
}

function SelectionPanel(props: {
  racks: readonly RackGeometry[];
  allBins: readonly MapBin[];
  sizeForm: SizeForm | null;
  onSizeForm: (f: SizeForm) => void;
  edited: number;
  busy: boolean;
  onClose: () => void;
  onRow: () => void;
  onMoveTo: (x: number, y: number) => void;
  onTurn: () => void;
  onResize: (size: { kind?: RackGeometry['kind']; width: number; depth: number; levelHeight?: number; gap?: number; walkway?: number }) => void;
  onSave: () => void;
  onDiscard: () => void;
  onPrint: () => void;
  onRemove: () => void;
  uses: readonly AisleUse[];
  onRenameRow: (to: string) => void;
  onEditArea: (change: { number?: number; name?: string | null }) => void;
}) {
  const { racks, sizeForm } = props;
  const first = racks[0]!;
  const area = first.kind === 'area';
  const single = racks.length === 1;
  const keys = new Set(racks.map(keyOf));
  const binCount = props.allBins.filter((b) => keys.has(keyOf(b))).length;
  const f = racks.map(rackFootprint);
  const corner = { x: Math.min(...f.map((p) => p.x0)), y: Math.min(...f.map((p) => p.y0)) };
  const [x, setX] = useState(metreText(corner.x));
  const [y, setY] = useState(metreText(corner.y));
  useEffect(() => {
    setX(metreText(corner.x));
    setY(metreText(corner.y));
  }, [corner.x, corner.y]);
  const sizes = sizeForm
    ? { width: metres(sizeForm.width), depth: metres(sizeForm.depth), levelHeight: metres(sizeForm.levelHeight), gap: metres(sizeForm.gap), walkway: metres(sizeForm.walkway) }
    : null;
  const sizesOk = sizes !== null && sizes.width > 0 && sizes.depth > 0 && (area || (sizes.levelHeight >= 0 && sizes.gap >= 0 && sizes.walkway > 0));
  const areaType = area ? props.allBins.find((b) => keys.has(keyOf(b)))?.type : undefined;
  const areaPreset = areaType ? (AREA_PRESETS as Partial<Record<string, AreaPreset>>)[areaType] : undefined;
  const areaLabel = area ? (areaPreset?.label ?? (areaType ? LOCATION_TYPE_LABELS[areaType] : 'Alan')) : null;
  // built on the old screen with a row letter (D-01 for a packing table): the letter stays taken until it gets its own code
  const oldCode = area && areaPreset !== undefined && areaPreset.code !== first.aisle;
  const nextNumber = areaPreset ? Math.max(0, ...props.uses.filter((u) => u.aisle === areaPreset.code).map((u) => u.lastRack)) + 1 : 1;
  const [letter, setLetter] = useState('');
  const [areaNumber, setAreaNumber] = useState('');
  const [areaName, setAreaName] = useState(first.name ?? '');
  useEffect(() => {
    setLetter('');
    setAreaNumber(oldCode ? String(nextNumber) : String(first.rack));
    setAreaName(first.name ?? '');
  }, [first.aisle, first.rack, first.name, oldCode, nextNumber]);
  const numberValue = Number(areaNumber);
  const numberOk = Number.isInteger(numberValue) && numberValue >= 1 && numberValue <= 99;
  const areaChanged = oldCode || numberValue !== first.rack || areaName.trim() !== (first.name ?? '');
  const rowBins = props.allBins.filter((b) => b.aisle === first.aisle).length;

  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <h3>{area ? `${nameOf(first)} · ${areaLabel}` : single ? `${nameOf(first)} · ${RACK_KIND_LABELS[first.kind]}` : `${first.aisle} sırası · ${racks.length} ${RACK_PRESETS[first.kind].unit}`}</h3>
          <div className="muted">
            {fmt.n(binCount)} göz · {metreText(first.width)} × {metreText(first.depth)} m{!area && first.levelHeight > 0 ? ` · kat ${metreText(first.levelHeight)} m` : ''}
          </div>
        </div>
        <button className="btn sm ghost" onClick={props.onClose} aria-label="Seçimi kapat">
          <Icon name="x" size={16} />
        </button>
      </div>
      {oldCode && (
        <div className="banner info-soft">
          <div>
            Bu alan eski ekranda <b>{first.aisle}</b> sıra harfiyle açılmış; bu yüzden {first.aisle} harfi raf sıralarına verilmiyor. Aşağıdan kaydedince kendi kodunu alır (
            {areaPreset.code}-{pad2(nextNumber)}), {first.aisle} harfi sıralara kalır.
          </div>
        </div>
      )}
      {area && areaPreset && (
        <>
          <h3>Kodu ve adı</h3>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="area-number">Numarası</label>
              <div className="unit-input prefix">
                <span className="mono">{areaPreset.code}-</span>
                <input id="area-number" className={`input mono${numberOk ? '' : ' invalid'}`} inputMode="numeric" value={areaNumber} onChange={(e) => setAreaNumber(e.target.value.replace(/\D/g, '').slice(0, 2))} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="area-name">Adı (isteğe bağlı)</label>
              <input id="area-name" className="input" maxLength={40} value={areaName} onChange={(e) => setAreaName(e.target.value)} placeholder={areaPreset.type === 'shipping' ? 'Trendyol Express' : areaPreset.label} />
            </div>
          </div>
          <div className="row">
            <button
              className="btn sm primary"
              disabled={props.busy || props.edited > 0 || !numberOk || !areaChanged}
              onClick={() => props.onEditArea({ number: numberValue, name: areaName.trim() || null })}
            >
              {oldCode || numberValue !== first.rack ? `${areaPreset.code}-${pad2(numberOk ? numberValue : 0)} olarak kaydet` : 'Adı kaydet'}
            </button>
          </div>
          <span className="hint">Ad haritada kodun yerine yazar. Numara değişirse alanın etiketini yeniden basın.</span>
        </>
      )}
      {!area && (
        <>
          <h3>Sıra harfi</h3>
          <div className="row">
            <select className="select sm" value={letter} onChange={(e) => setLetter(e.target.value)} aria-label="Yeni sıra harfi">
              <option value="">{first.aisle} (şimdiki)</option>
              {letterChoices(props.uses, 1).map(({ aisle, use }) => (
                <option key={aisle} value={aisle} disabled={use !== null}>
                  {use ? `${aisle} · ${lowerLabel(use.label)}` : aisle}
                </option>
              ))}
            </select>
            <button className="btn sm" disabled={props.busy || props.edited > 0 || !letter} onClick={() => props.onRenameRow(letter)}>
              {letter ? `${first.aisle} sırasını ${letter} yap` : 'Harfi değiştir'}
            </button>
          </div>
          {letter && (
            <span className="hint">
              Bütün {first.aisle} sırası değişir: {fmt.n(rowBins)} göz {letter}-01-1-1 gibi yeni kod alır, stok ve geçmişi gözlerinde kalır. Raflardaki etiketleri yeniden basıp
              değiştirin.
            </span>
          )}
        </>
      )}
      {props.edited > 0 && (area || !single) && <span className="hint">Kod veya harf değiştirmeden önce taşıma ve ölçü değişikliğini kaydedin.</span>}
      {!area && single && (
        <button className="btn sm" onClick={props.onRow}>
          Bütün {first.aisle} sırasını seç
        </button>
      )}
      {!area && !single && <span className="hint">Yalnız bir rafı düzenlemek için o rafa bir kez daha dokunun.</span>}

      <h3>
        <Icon name="move" size={16} /> Yeri
      </h3>
      <span className="hint">Haritada sürükleyin ya da köşesinin yerini yazın.</span>
      <div className="form-grid">
        <MetreField id="sel-x" label="Köşesi X" value={x} onChange={setX} allowNegative />
        <MetreField id="sel-y" label="Köşesi Y" value={y} onChange={setY} allowNegative />
      </div>
      <div className="row">
        <button className="btn sm" disabled={!Number.isFinite(metres(x)) || !Number.isFinite(metres(y))} onClick={() => props.onMoveTo(metres(x), metres(y))}>
          Bu yere taşı
        </button>
        <button className="btn sm" onClick={props.onTurn}>
          <Icon name="flip" size={16} /> {area ? '90° çevir' : 'Yönünü çevir'}
        </button>
      </div>

      {sizeForm && (
        <>
          <h3>Ölçüleri</h3>
          <SizeFormView form={sizeForm} onChange={props.onSizeForm} area={area} single={single} />
          <div className="row">
            <button
              className="btn sm"
              disabled={!sizesOk}
              onClick={() =>
                sizes &&
                props.onResize(
                  area
                    ? { width: sizes.width, depth: sizes.depth }
                    : { kind: sizeForm.kind, width: sizes.width, depth: sizes.depth, levelHeight: sizes.levelHeight, gap: sizes.gap, walkway: sizes.walkway },
                )
              }
            >
              Ölçüleri uygula
            </button>
          </div>
          <span className="hint">Rafların koridora bakan yüzü ve ilk rafın başı yerinde kalır; kat yüksekliği gözlerin yüksekliğini de değiştirir.</span>
        </>
      )}

      {props.edited > 0 && (
        <div className="banner warn">
          <div>
            Değişiklik henüz kaydedilmedi.
            <div className="row" style={{ marginTop: 6 }}>
              <button className="btn sm primary" disabled={props.busy} onClick={props.onSave}>
                Kaydet
              </button>
              <button className="btn sm" onClick={props.onDiscard}>
                Geri al
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="row">
        <button className="btn sm" onClick={props.onPrint}>
          <Icon name="printer" size={16} /> Etiketlerini bas
        </button>
        <button className="btn sm ghost danger-text" onClick={props.onRemove}>
          <Icon name="trash" size={16} /> Sil
        </button>
      </div>
    </div>
  );
}

/** Where pick routes start (P) and the cross aisles a picker changes aisle through. */
function RouteSettings(props: { warehouse: WarehouseView; racks: readonly RackGeometry[]; depot: Coordinates3D | null; onDepot: (d: Coordinates3D) => void; onSaved: () => void }) {
  const { warehouse, racks, depot } = props;
  const toast = useToast();
  const axis = warehouse.layout.aisleAxis ?? 'y';
  const [cross, setCross] = useState((warehouse.layout.crossAisles ?? []).map(metreText).join('; '));
  const [px, setPx] = useState(metreText(depot?.x ?? 0));
  const [py, setPy] = useState(metreText(depot?.y ?? 0));
  const [busy, setBusy] = useState(false);
  // P dragged on the map
  useEffect(() => {
    if (depot && metres(px) !== depot.x) setPx(metreText(depot.x));
    if (depot && metres(py) !== depot.y) setPy(metreText(depot.y));
  }, [depot?.x, depot?.y]);
  const crossValues = cross
    .split(/[;\s]+/)
    .filter(Boolean)
    .map(metres);
  const saved = warehouse.layout.depot;
  const depotChanged = depot !== null && (!saved || saved.x !== depot.x || saved.y !== depot.y);
  const valid = crossValues.every(Number.isFinite) && depot !== null;

  /** The front and back of the rows: where a picker walks from one aisle to the next. */
  const suggest = () => {
    const storage = racks.filter((r) => r.kind !== 'area').map(rackFootprint);
    if (storage.length === 0) return toast('Önce raf sırası ekleyin', 'info');
    const lo = Math.min(...storage.map((f) => (axis === 'y' ? f.y0 : f.x0)));
    const hi = Math.max(...storage.map((f) => (axis === 'y' ? f.y1 : f.x1)));
    setCross(`${metreText(snap(lo - 0.6))}; ${metreText(snap(hi + 0.6))}`);
  };

  const save = async () => {
    if (!depot) return;
    setBusy(true);
    try {
      await api('PUT', `/api/admin/warehouses/${warehouse.id}/layout`, { aisleAxis: axis, crossAisles: crossValues, depot });
      toast('Rota ayarları kaydedildi; yeni toplama rotaları bunlara göre hesaplanır', 'ok');
      props.onSaved();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="editor-section" open={depotChanged}>
      <summary>Rota ayarları: başlangıç noktası (P) ve ara koridorlar</summary>
      <div className="stack" style={{ marginTop: 10 }}>
        <span className="hint">Toplama rotaları P’den başlar ve P’ye döner; P’yi haritada paketleme masalarının yanına sürükleyin.</span>
        <div className="form-grid">
          <MetreField
            id="p-x"
            label="P konumu X"
            value={px}
            onChange={(v) => {
              setPx(v);
              if (Number.isFinite(metres(v))) props.onDepot({ x: metres(v), y: depot?.y ?? 0, z: 0 });
            }}
            allowNegative
          />
          <MetreField
            id="p-y"
            label="P konumu Y"
            value={py}
            onChange={(v) => {
              setPy(v);
              if (Number.isFinite(metres(v))) props.onDepot({ x: depot?.x ?? 0, y: metres(v), z: 0 });
            }}
            allowNegative
          />
        </div>
        <div className="field">
          <label htmlFor="l-cross">Ara koridorlar ({axis === 'y' ? 'Y' : 'X'} konumu, metre)</label>
          <input id="l-cross" className="input" value={cross} onChange={(e) => setCross(e.target.value)} placeholder="0; 18" />
          <span className="hint">Toplayıcının bir koridordan diğerine geçebildiği yerler: genelde sıraların önü ve arkası. Noktalı virgülle ayırın.</span>
        </div>
        <div className="row">
          <button className="btn sm" onClick={suggest}>
            Raflardan öner
          </button>
          <button className="btn sm primary" disabled={busy || !valid} onClick={() => void save()}>
            Kaydet
          </button>
          {depotChanged && <span className="chip warn">P’nin yeni yeri kaydedilmedi</span>}
        </div>
      </div>
    </details>
  );
}
